import { ProductCategory } from '@aglenergy/shared-library';
import { Component, Host, h, Prop, JSX, State } from '@stencil/core';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { store } from 'app/shared/store/store';
import { changePlanService } from 'app/nbn/services/change-plan/change-plan.service';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';

@Component({
  tag: 'plan-categories',
  styleUrl: 'plan-categories.scss',
  shadow: false,
  scoped: true
})
export class PlanCategories {
  @Prop() public planCategories: string[];
  @State() private renderedPlans = 0;
  private categorySelected = '';

  private selectCategory(category: string) {
    store.set(StoreKeys.selectedPlanCategory, category);
    if (this.planCategories.length >= 2) {
      window.location.hash = encodeURIComponent(category);
    } else {
      window.history.replaceState('', document.title, window.location.pathname + window.location.search);
    }
    tealiumAnalyticService.trackPlanCategoriesClick(category, ProductCategory.Nbn);
  }

  private observer = new MutationObserver((_) => {
    const cards = document.querySelectorAll('.card__wrapper');
    if (cards.length !== this.renderedPlans) {
      this.calculateAndSetDividerWidth();
    }
  });

  private calculateAndSetDividerWidth = (): void => {
    const viewportWidth = document.body.clientWidth;
    const allCards = document.getElementsByClassName('card__wrapper');
    const firstCard = allCards[0];
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width;

    const cardMargin = 32;

    let dividerWidth = 0;
    let numberOfCards = 1;
    while (true && numberOfCards <= allCards.length) {
      const cardsWidth = numberOfCards * cardWidth + (numberOfCards - 1) * cardMargin;
      if (cardsWidth < viewportWidth) {
        dividerWidth = cardsWidth;
        numberOfCards += 2;
      } else {
        break;
      }
    }
    numberOfCards -= 2;
    if (numberOfCards >= 1 && this.planCategories.length > 1) {
      const buttonElement = document.getElementById('categories-buttons');
      if (buttonElement) {
        buttonElement.style.width = `${dividerWidth}px`;
      }
    }
    this.observer.disconnect();
  };

  componentDidLoad(): void {
    window.addEventListener('resize', this.onResize, true);
  }

  private onResize = () => {
    this.calculateAndSetDividerWidth();
    this.observer.observe(document.querySelector('.comparison-section-wrapper'), {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false
    });
  };

  componentDidRender(): void {
    this.calculateAndSetDividerWidth();
    this.observer.observe(document.querySelector('.comparison-section-wrapper'), {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false
    });
  }

  componentWillLoad(): void {
    this.selectedCategoryHashChanged();
    window.onhashchange = this.selectedCategoryHashChanged;
  }

  private selectedCategoryHashChanged = (): void => {
    const hashValue = window.location.hash.replace('#', '');
    const selectedCategory = this.getSelectedCategoryByHashValue(decodeURIComponent(hashValue));
    if (selectedCategory) {
      store.set(StoreKeys.selectedPlanCategory, selectedCategory);
    }
  };

  private getSelectedCategoryByHashValue = (hashValue: string): string => {
    const selectedCategory = this.planCategories.find((x) => x.toUpperCase() === hashValue.toUpperCase());
    return selectedCategory;
  };

  private selectDefaultCategory(): string {
    let selectedCategory: string;
    const storedCategory = store.get(StoreKeys.selectedPlanCategory);

    if (this.planCategories.includes('Fibre upgrade')) {
      selectedCategory = 'Fibre upgrade';
    } else if (storedCategory) {
      selectedCategory = storedCategory;
    } else {
      selectedCategory = this.planCategories[0];
    }

    const unknownCategory = !this.planCategories.includes(selectedCategory);
    if (unknownCategory) {
      selectedCategory = this.planCategories[0];
    }

    if (this.planCategories.length >= 2) {
      window.location.hash = encodeURIComponent(selectedCategory);
    } else {
      window.history.replaceState('', document.title, window.location.pathname + window.location.search);
    }
    store.set(StoreKeys.selectedPlanCategory, selectedCategory);
    return selectedCategory;
  }

  public categoryClicked(value: string): void {
    this.categorySelected = store.get('address') as string;
    this.selectCategory(value);
  }

  render(): JSX.Element {
    let selectedCategory: string;
    if (this.categorySelected === (store.get('address') as string)) {
      selectedCategory = store.get(StoreKeys.selectedPlanCategory);
    } else {
      selectedCategory = this.selectDefaultCategory();
    }
    const isSelected = (mappedValue: string): boolean => selectedCategory === mappedValue;

    if (!selectedCategory || !this.planCategories.includes(selectedCategory)) {
      selectedCategory = this.planCategories[0];
    }

    const isChangePlan = changePlanService.isChangePlan();
    const changePlanSku = isChangePlan ? changePlanService.getSelectedProductSku() : '';
    const containerClass = isChangePlan ? 'categories-container change-plan' : 'categories-container';
    const currentPlanSku = isChangePlan ? changePlanService.getCurrentPlanSku() : '';
    return (
      this.planCategories &&
      this.planCategories.length > 1 &&
      !(isChangePlan && changePlanSku?.length > 0 && currentPlanSku !== changePlanSku) && (
        <Host>
          <div class={containerClass} role="tablist">
            <div id="categories-dropdown-div">
              <dls-dropdown
                id="categories-dropdown"
                label="Plan categories" // design has no label. mandatory to avoid console error
                optional={false}
                defaultValue={selectedCategory}
                onChange={() => this.categoryClicked((document.querySelector('#categories-dropdown select') as HTMLSelectElement).value)}
              >
                {this.planCategories.map((pc) => (
                  <option value={pc} selected={isSelected(pc)} role="tab">
                    {pc}
                  </option>
                ))}
              </dls-dropdown>
            </div>
            <div id="categories-buttons" aria-owns="panning-wrapper">
              {this.planCategories.map((pc) => {
                return (
                  <button
                    class={isSelected(pc) ? 'selected' : ''}
                    onClick={() => this.categoryClicked(pc)}
                    role="tab"
                    aria-label={`Show ${pc} plans`}
                    aria-selected={`${isSelected(pc)}`}
                  >
                    {pc}
                  </button>
                );
              })}
            </div>
          </div>
        </Host>
      )
    );
  }
}
